import { memo } from 'react';
import { Group, Line, Rect, Text } from 'react-konva';
import { EQUIPMENT_2D_COLORS as C, EQUIPMENT_2D_SIMPLE_ZOOM } from './equipment2dPalette';
import {
  EQUIPMENT_2D_DESIGN as D,
  defaultDockFractions,
  getEquipment2DLayout,
  loadPortDesignX,
  resolveEquipmentPortCount,
} from './equipmentGeometry';
import { isEquipmentFoupSlotOccupied } from '../Equipment/equipmentFoupSlots';
import type { Equipment2DProps } from './types';

/**
 * Orthographic semiconductor process tool — procedural only, strict footprint bounds.
 */
export const Equipment2D = memo(function Equipment2D({
  x = 0,
  y = 0,
  width = 12,
  height = 8,
  kind = 'equipment',
  loadPortCount,
  dockEdge = 'bottom',
  occupiedPorts,
  foupSlotView,
  empty = false,
  selected = false,
  hovered = false,
  zoomLevel = 1,
  opacity = 1,
  scale = 1,
  rotation = 0,
  centered = false,
  listening = false,
  stroke = C.outline,
  strokeWidth,
  onClick,
  onDblClick,
  onMouseEnter,
  onMouseLeave,
}: Equipment2DProps) {
  const simple = zoomLevel < EQUIPMENT_2D_SIMPLE_ZOOM;
  const { s, ox, oy, cw, ch } = getEquipment2DLayout(width, height, dockEdge);
  const portCount = resolveEquipmentPortCount(kind, loadPortCount);
  const portFractions = defaultDockFractions(portCount);
  const u = (n: number) => n * s;
  const sw = strokeWidth ?? Math.min(u(D.stroke), 1.5 / Math.max(zoomLevel, 0.05));
  const originX = centered ? x - width / 2 : x;
  const originY = centered ? y - height / 2 : y;

  const mainH = D.height - D.baseH;
  const chamberBottom = D.chamberTop + D.chamberH;

  const loadPorts = portFractions.map((rx, i) => {
    const cx = loadPortDesignX(rx, width, ox, s);
    return { key: `lp-${i}`, cx, rx };
  });

  const selectionRing = (ringX: number, ringY: number, ringW: number, ringH: number, radius: number) =>
    (selected || hovered) && (
      <Rect
        x={ringX}
        y={ringY}
        width={ringW}
        height={ringH}
        fillEnabled={false}
        stroke={selected ? C.selection : C.hover}
        strokeWidth={sw}
        cornerRadius={radius}
        listening={false}
        perfectDrawEnabled={false}
        shadowForStrokeEnabled={false}
      />
    );

  const portIndexFromKey = (key: string) => {
    const match = /^lp-(\d+)$/.exec(key);
    return match ? Number(match[1]) : 0;
  };

  const isPortOccupied = (portIndex: number) => {
    if (empty) return false;
    if (foupSlotView) return isEquipmentFoupSlotOccupied(portIndex, foupSlotView);
    return occupiedPorts?.includes(portIndex) ?? false;
  };

  const renderLoadPorts = (compact: boolean) =>
    loadPorts.map(({ key, cx }) => {
      const portIndex = portIndexFromKey(key);
      const occupied = isPortOccupied(portIndex);
      const padFill = occupied ? C.foup : C.loadPad;
      const padInner = occupied ? C.foupLid : C.loadPadInner;
      const padStroke = occupied ? C.foupStroke : C.loadRim;
      const pw = compact ? D.portW * 0.85 : D.portW;
      const ph = compact ? D.portH * 0.9 : D.portH;
      const py = compact ? D.deckY + 4 : D.portPadY;
      return (
        <Group key={key} listening={false}>
          <Rect
            x={u(cx - pw / 2)}
            y={u(py)}
            width={u(pw)}
            height={u(ph)}
            fill={padFill}
            cornerRadius={u(1.5)}
            stroke={padStroke}
            strokeWidth={sw * 0.5}
            perfectDrawEnabled={false}
          />
          {!compact && (
            <>
              <Rect
                x={u(cx - pw / 2 + 2)}
                y={u(py + 2)}
                width={u(pw - 4)}
                height={u(ph - 4)}
                fill={padInner}
                cornerRadius={u(1)}
                listening={false}
                perfectDrawEnabled={false}
              />
              <Line
                points={[u(cx - pw / 2 + 4), u(py + ph - 3), u(cx + pw / 2 - 4), u(py + ph - 3)]}
                stroke={padStroke}
                strokeWidth={sw * 0.35}
                listening={false}
                perfectDrawEnabled={false}
              />
            </>
          )}
        </Group>
      );
    });

  return (
    <Group
      x={originX}
      y={originY}
      scaleX={scale}
      scaleY={scale}
      rotation={rotation}
      opacity={opacity}
      listening={listening}
      onClick={onClick}
      onDblClick={onDblClick}
      onTap={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <Rect width={width} height={height} fill="transparent" listening={listening} perfectDrawEnabled={false} />

      <Group x={ox} y={oy} listening={false}>
        {simple ? (
          <>
            <Rect
              width={cw}
              height={ch}
              fill={C.frame}
              cornerRadius={u(3)}
              stroke={stroke}
              strokeWidth={sw}
              perfectDrawEnabled={false}
            />
            <Rect
              x={u(D.margin)}
              y={u(D.margin)}
              width={Math.max(0, cw - u(D.margin * 2))}
              height={Math.max(0, ch - u(D.baseH + D.margin))}
              fill={C.panel}
              cornerRadius={u(2)}
              listening={false}
              perfectDrawEnabled={false}
            />
            <Rect
              x={u(D.margin + 2)}
              y={u(D.chamberTop)}
              width={u(D.efemW - 4)}
              height={u(D.chamberH)}
              fill={C.bodyShade}
              cornerRadius={u(2)}
              listening={false}
              perfectDrawEnabled={false}
            />
            <Rect
              x={u(D.windowX)}
              y={u(D.windowY)}
              width={u(D.windowW)}
              height={u(D.windowH)}
              fill={C.glass}
              stroke={C.glassStroke}
              strokeWidth={sw * 0.45}
              cornerRadius={u(2)}
              listening={false}
              perfectDrawEnabled={false}
            />
            <Rect
              x={0}
              y={ch - u(D.baseH)}
              width={cw}
              height={u(D.baseH)}
              fill={C.baseDark}
              listening={false}
              perfectDrawEnabled={false}
            />
            {renderLoadPorts(true)}
            {selectionRing(-2, -2, cw + 4, ch + 4, u(3))}
          </>
        ) : (
          <>
            {/* Outer structural frame — full footprint */}
            <Rect
              x={u(D.margin)}
              y={u(D.margin)}
              width={u(D.width - D.margin * 2)}
              height={u(mainH - D.margin)}
              fill={C.frame}
              cornerRadius={u(3)}
              stroke={stroke}
              strokeWidth={sw}
              perfectDrawEnabled={false}
              shadowForStrokeEnabled={false}
            />
            <Rect
              x={u(D.margin + 2)}
              y={u(D.margin + 2)}
              width={u(D.width - D.margin * 2 - 4)}
              height={u(3)}
              fill={C.frameHighlight}
              cornerRadius={u(1)}
              listening={false}
              perfectDrawEnabled={false}
            />

            {/* EFEM / operator service module (left) */}
            <Rect
              x={u(D.margin + 4)}
              y={u(D.efemTop)}
              width={u(D.efemW)}
              height={u(D.chamberH + 8)}
              fill={C.body}
              cornerRadius={u(2)}
              stroke={C.outline}
              strokeWidth={sw * 0.45}
              perfectDrawEnabled={false}
            />
            <Rect
              x={u(D.margin + 8)}
              y={u(D.efemTop + 4)}
              width={u(D.efemW - 8)}
              height={u(D.chamberH)}
              fill={C.panelRecess}
              cornerRadius={u(2)}
              listening={false}
              perfectDrawEnabled={false}
            />
            {/* Vents */}
            {[0, 1].map((i) => (
              <Rect
                key={`vent-${i}`}
                x={u(D.margin + 10 + i * 18)}
                y={u(D.efemTop + 6)}
                width={u(14)}
                height={u(8)}
                fill={C.vent}
                cornerRadius={u(1)}
                listening={false}
                perfectDrawEnabled={false}
              />
            ))}
            {/* Recessed HMI */}
            <Rect
              x={u(D.hmiX)}
              y={u(D.hmiY)}
              width={u(D.hmiW)}
              height={u(D.hmiH)}
              fill={C.screenBezel}
              cornerRadius={u(2)}
              stroke={C.outline}
              strokeWidth={sw * 0.4}
              perfectDrawEnabled={false}
            />
            <Rect
              x={u(D.hmiX + 2)}
              y={u(D.hmiY + 2)}
              width={u(D.hmiW - 4)}
              height={u(D.hmiH - 4)}
              fill={C.screen}
              cornerRadius={u(1.5)}
              listening={false}
              perfectDrawEnabled={false}
            />
            <Rect
              x={u(D.hmiX + 4)}
              y={u(D.hmiY + 4)}
              width={u(D.hmiW - 8)}
              height={u(4)}
              fill={C.screenGlow}
              opacity={0.5}
              cornerRadius={u(1)}
              listening={false}
              perfectDrawEnabled={false}
            />
            {foupSlotView && foupSlotView.capacity > 0 ? (
              <Text
                x={u(D.hmiX + 2)}
                y={u(D.hmiY + 8)}
                width={u(D.hmiW - 4)}
                text={`${foupSlotView.occupied}/${foupSlotView.capacity}`}
                fontSize={Math.max(6, u(5))}
                fill={C.screenGlow}
                align="center"
                listening={false}
                perfectDrawEnabled={false}
              />
            ) : null}
            {/* Status LEDs */}
            {[
              { x: D.hmiX, c: C.ledReady },
              { x: D.hmiX + 10, c: C.ledBusy },
              { x: D.hmiX + 20, c: C.ledFault },
            ].map((led, i) => (
              <Rect
                key={`led-${i}`}
                x={u(led.x)}
                y={u(D.hmiY + D.hmiH + 6)}
                width={u(6)}
                height={u(6)}
                fill={led.c}
                cornerRadius={u(3)}
                stroke={C.outline}
                strokeWidth={sw * 0.25}
                listening={false}
                perfectDrawEnabled={false}
              />
            ))}
            <Line
              points={[u(D.margin + D.efemW + 6), u(D.efemTop + 4), u(D.margin + D.efemW + 6), u(chamberBottom + 4)]}
              stroke={C.seam}
              strokeWidth={sw * 0.35}
              listening={false}
              perfectDrawEnabled={false}
            />

            {/* Process chamber module */}
            <Rect
              x={u(D.chamberX)}
              y={u(D.chamberTop)}
              width={u(D.chamberW)}
              height={u(D.chamberH)}
              fill={C.panel}
              cornerRadius={u(2)}
              stroke={C.outline}
              strokeWidth={sw * 0.45}
              perfectDrawEnabled={false}
            />
            <Rect
              x={u(D.chamberX + 4)}
              y={u(D.chamberTop + 4)}
              width={u(D.chamberW - 8)}
              height={u(6)}
              fill={C.panelWarm}
              listening={false}
              perfectDrawEnabled={false}
            />
            <Rect
              x={u(D.windowX)}
              y={u(D.windowY)}
              width={u(D.windowW)}
              height={u(D.windowH)}
              fill={C.glass}
              stroke={C.glassStroke}
              strokeWidth={sw * 0.55}
              cornerRadius={u(2)}
              perfectDrawEnabled={false}
            />
            <Line
              points={[
                u(D.windowX + 8),
                u(D.windowY + 10),
                u(D.windowX + D.windowW - 12),
                u(D.windowY + D.windowH - 14),
              ]}
              stroke={C.glassSheen}
              strokeWidth={sw * 0.5}
              listening={false}
              perfectDrawEnabled={false}
            />
            <Line
              points={[u(D.chamberX + 8), u(D.chamberTop + D.chamberH * 0.55), u(D.chamberX + D.chamberW - 8), u(D.chamberTop + D.chamberH * 0.55)]}
              stroke={C.seam}
              strokeWidth={sw * 0.3}
              listening={false}
              perfectDrawEnabled={false}
            />
            {/* Maintenance access panel */}
            <Rect
              x={u(D.chamberX + D.chamberW - 22)}
              y={u(D.chamberTop + 10)}
              width={u(14)}
              height={u(28)}
              fill={C.panelWarm}
              stroke={C.seam}
              strokeWidth={sw * 0.35}
              cornerRadius={u(1)}
              listening={false}
              perfectDrawEnabled={false}
            />
            <Line
              points={[u(D.chamberX + D.chamberW - 15), u(D.chamberTop + 14), u(D.chamberX + D.chamberW - 15), u(D.chamberTop + 34)]}
              stroke={C.seam}
              strokeWidth={sw * 0.35}
              listening={false}
              perfectDrawEnabled={false}
            />

            {/* Integrated load-port deck (dock edge) */}
            <Rect
              x={u(D.margin + 4)}
              y={u(D.deckY)}
              width={u(D.width - D.margin * 2 - 8)}
              height={u(D.deckH)}
              fill={C.loadDeck}
              cornerRadius={[0, 0, u(2), u(2)]}
              stroke={C.outline}
              strokeWidth={sw * 0.4}
              perfectDrawEnabled={false}
            />
            {renderLoadPorts(false)}

            {/* Plinth / base */}
            <Rect
              x={0}
              y={u(D.height - D.baseH)}
              width={u(D.width)}
              height={u(D.baseH)}
              fill={C.base}
              listening={false}
              perfectDrawEnabled={false}
            />
            <Rect
              x={0}
              y={u(D.height - 3)}
              width={u(D.width)}
              height={u(3)}
              fill={C.baseDark}
              listening={false}
              perfectDrawEnabled={false}
            />

            {selectionRing(u(-2), u(-2), u(D.width + 4), u(D.height + 4), u(3))}
          </>
        )}
      </Group>
    </Group>
  );
});
